import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { FetchError, GetIssuesDto, IssueDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { useSignalR } from "../../../hooks";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  issueGuid: string;
  teamSlug?: string;
  createdByUserId: string;
};

const useDeleteIssueRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteIssueRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteIssueRequest };
};

const useDeleteIssue = () => {
  const { deleteIssueRequest } = useDeleteIssueRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteIssueRequest,
    onMutate: async (variables) => {
      let cachedTeamData: GetIssuesDto | undefined;
      let cachedUserData: GetIssuesDto | undefined;

      cachedTeamData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(variables.teamSlug, undefined),
        (cachedData) => ({
          ...cachedData,
          issues: cachedData.issues.filter((issue) => issue.guid !== variables.issueGuid),
        })
      );

      cachedUserData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        (cachedData) => ({
          ...cachedData,
          issues: cachedData.issues.filter((issue) => issue.guid !== variables.issueGuid),
        })
      );

      const cachedIssueDetailsData = queryClient.getQueryData<IssueDto>(
        queryKeys.issues.getIssueDetail(variables.issueGuid)
      );

      queryClient.removeQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));

      return { cachedTeamData, cachedUserData, cachedIssueDetailsData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueDetailsData);
      queryClient.setQueryData(queryKeys.issues.getIssues(variables.teamSlug, undefined), context?.cachedTeamData);
      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        context?.cachedUserData
      );
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }
      signalR.sendRefetchUserIssues(variables.createdByUserId);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUserId));
    },
  });
};

export { useDeleteIssue };
